
import React from 'react';
import { ToBillArguments, ToBillEvent } from '../../models/Factura';
import { formatCurrencyStyled } from '../visuals/currency.formatted';
import { conceptoToTotales } from '../../utils/calculate.conceptos';
import unifiedMoment from '../../services/unifiedMoment';
import ButtonOptionDropdown from '../Forms/ButtonOptionDropdown';

const moment = unifiedMoment;

interface DashboardBillEventProps {
    billEvent: ToBillEvent;
    parentArgument: ToBillArguments<ToBillEvent>;
    onEmitBillEvent: (billEvent: ToBillEvent, parentArgument: ToBillArguments<ToBillEvent>) => void;
    onEmitPago: (billEvent: ToBillEvent, parentArgument: ToBillArguments<ToBillEvent>) => void;
    onCloseBillEvent: (billEvent: ToBillEvent, parentArgument: ToBillArguments<ToBillEvent>) => void;
    onMuteBillEvent: (billEvent: ToBillEvent, parentArgument: ToBillArguments<ToBillEvent>) => void;
}

function DashboardBillEvent({ billEvent, parentArgument, onEmitBillEvent, onEmitPago, onCloseBillEvent, onMuteBillEvent }: DashboardBillEventProps) {

    const handleCloseBillEvent = (billEvent: ToBillEvent, parentArgument: ToBillArguments<ToBillEvent>) => () => {
        onCloseBillEvent(billEvent, parentArgument);
    };

    const handleMuteBillEvent = (billEvent: ToBillEvent, parentArgument: ToBillArguments<ToBillEvent>) => () => {
        onMuteBillEvent(billEvent, parentArgument);
    };

    if (billEvent.CFDI) {
        const { total } = conceptoToTotales(billEvent.CFDI.conceptos);
        return (
            <div className="billPreview">
                <div className="_receptor">
                    <p className="_name">{billEvent.CFDI.receptor.nombre}</p>
                    <p className="text3">{billEvent.CFDI.conceptos.length > 1 ? `${billEvent.CFDI.conceptos.length} conceptos` : billEvent.CFDI.conceptos[0].descripcion}</p>
                </div>
                {
                    parentArgument.upfront ?
                        // BillEvent with upfront payment are Liquidación types
                        // >> BillEvent.when only exists on PaymentEvents
                        <div className="_billInfo">
                            <p className="_bill">Total a pagar</p>
                            <p className={`date `}>adelanto {moment(parentArgument.createdAt).fromNow()}</p>
                        </div>
                        :
                        <div className="_billInfo">
                            <p className="_bill">Factura</p>
                            <p className={`date `}>{parentArgument.createdAt ? moment(parentArgument.createdAt).fromNow() : ''}</p>
                        </div>
                }
                <div className="_monto">
                    <p className="text2">{formatCurrencyStyled(total)}</p>
                    {
                        // If Bill Event is stamped and canceled, we can re-emit it
                        // >> If Bill Event is stamped and not canceled, show no button
                        // >> Normally, only open events are loaded, so we shouldn't see
                        billEvent.uuid ?
                            (billEvent.wasCanceled ?
                                <ButtonOptionDropdown
                                    size='small'
                                    secondary={true}
                                    onClickMain={() => onEmitBillEvent(billEvent, parentArgument)}
                                    options={[
                                        { label: 'Silenciar', onClick: handleMuteBillEvent(billEvent, parentArgument) },
                                        { label: 'Marcar cerrado', onClick: handleCloseBillEvent(billEvent, parentArgument) },
                                    ]}
                                >
                                    <span>Re-Emitir</span>
                                </ButtonOptionDropdown>
                                : '') :
                            <ButtonOptionDropdown
                                size='small'
                                primary={true}
                                onClickMain={() => onEmitBillEvent(billEvent, parentArgument)}
                                options={[
                                    { label: 'Silenciar', onClick: handleMuteBillEvent(billEvent, parentArgument) },
                                    { label: 'Marcar cerrado', onClick: handleCloseBillEvent(billEvent, parentArgument) },
                                ]}
                            >
                                <span>Emitir</span>
                            </ButtonOptionDropdown>
                    }
                </div>
            </div>
        );
    } else if (billEvent.paymentCFDI) {
        // Payment event
        // >> ELSE-IF is explicit as there was a bug when canceling Credit Notes reopened bills
        const isOld = billEvent.when ? billEvent.when < Date.now() : false;
        return (
            <div className="billPreview payment">
                <div className="_receptor">
                    <p className="_name">{billEvent.paymentCFDI.receptor.nombre}</p>
                    <p className="text3">Pago de factura</p>
                </div>
                <div className="_billInfo">
                    <p className="_payment">Pago parcialidad <span className="small">{billEvent.paymentNum} de {parentArgument.paymentEvents.length}</span></p>
                    {
                        billEvent.wasCanceled ?
                            <p className="warning">
                                Cancelada anteriormente
                            </p>
                            :
                            (isOld ?
                                <p className={`date old`}><i className="material-icons">warning</i> {moment(billEvent.when).fromNow()}</p>
                                :
                                <p className={`date `}>{billEvent.when ? `esperado ${moment(billEvent.when).fromNow()}` : '- -'}</p>)
                    }
                </div>
                <div className="_monto">
                    <p className="text2">{formatCurrencyStyled(billEvent.paymentCFDI.pagos[0].monto)}</p>
                    {
                        billEvent.uuid ?
                            (billEvent.wasCanceled ?
                                <ButtonOptionDropdown
                                    size='small'
                                    secondary={true}
                                    onClickMain={() => onEmitPago(billEvent, parentArgument)}
                                    options={[
                                        { label: 'Silenciar', onClick: handleMuteBillEvent(billEvent, parentArgument) },
                                        { label: 'Marcar cerrado', onClick: handleCloseBillEvent(billEvent, parentArgument) },
                                    ]}
                                >
                                    <span>Re-Emitir</span>
                                </ButtonOptionDropdown>
                                : '') :
                            <ButtonOptionDropdown
                                size='small'
                                primary={true}
                                onClickMain={() => onEmitPago(billEvent, parentArgument)}
                                options={[
                                    { label: 'Silenciar', onClick: handleMuteBillEvent(billEvent, parentArgument) },
                                    { label: 'Marcar cerrado', onClick: handleCloseBillEvent(billEvent, parentArgument) },
                                ]}
                            >
                                <span>Emitir</span>
                            </ButtonOptionDropdown>
                    }
                </div>
            </div>
        );
    }
    return null;
}

export default DashboardBillEvent;
